import { v4 as uuidv4, v5 as uuidv5 } from "uuid";

import { captureException } from "@/monitoring";
import { sessionEndEventSchema, sessionStartEventSchema } from "@/schemas";

import { impressionConfig } from "./config";
import { enqueue } from "./queue";
import { readSession, writeSession, type SessionState } from "./session-state";

import type { SessionEndEvent, SessionStartEvent } from "@/schemas";

// ── Deterministic session_end id ──

/**
 * Shared cross-platform namespace for the deterministic `session_end` event_id, itself
 * UUIDv5(RFC-4122 DNS namespace, "analytics.truthsocial.com"). This value MUST stay byte-identical
 * across iOS / Android / web (see TMTG-22) — the server's dedup collapses repeated ends into one
 * row by matching event_id, so a drifted namespace silently breaks that collapse.
 */
const SESSION_END_NAMESPACE = "19410d26-04a1-5800-8e0c-d7ff90b8e437";

/** `session_end.event_id = UUIDv5(NAMESPACE, lowercased session_id)` — same id on every emission. */
const sessionEndEventId = (sessionId: string): string =>
  uuidv5(sessionId.toLowerCase(), SESSION_END_NAMESPACE);

// ── Cross-tab session registry (localStorage, shared across tabs) ──

/**
 * One entry per session that has (or recently had) a live tab. `lastActivityAt` lets any tab, on
 * load, tell an abandoned session (stale past the idle boundary) apart from one another tab is
 * still driving; `seq` / `appVersion` are what a deferred `session_end` needs to be built by a tab
 * that never owned the session.
 */
interface SessionRecord {
  lastActivityAt: number;
  seq: number;
  appVersion: string;
}

type SessionRegistry = Record<string, SessionRecord>;

const readRegistry = (): SessionRegistry => {
  try {
    const raw = localStorage.getItem(impressionConfig.sessionRegistryKey);
    if (raw) return JSON.parse(raw) as SessionRegistry;
  } catch {
    /* storage disabled / malformed */
  }
  return {};
};

const writeRegistry = (registry: SessionRegistry): void => {
  try {
    localStorage.setItem(
      impressionConfig.sessionRegistryKey,
      JSON.stringify(registry),
    );
  } catch {
    /* storage disabled — deferred end just won't be recoverable from this tab */
  }
};

const registerSession = (
  id: string,
  lastActivityAt: number,
  seq: number,
): void => {
  const registry = readRegistry();
  registry[id] = {
    lastActivityAt,
    seq,
    appVersion: impressionConfig.appVersion,
  };
  writeRegistry(registry);
};

const unregisterSession = (id: string): void => {
  const registry = readRegistry();
  if (id in registry) {
    delete registry[id];
    writeRegistry(registry);
  }
};

// ── Event emission ──

const emitSessionStart = (
  sessionId: string,
  clientSeq: number,
  eventTimeMs: number,
): void => {
  const event: SessionStartEvent = {
    event_id: uuidv4(), // random UUIDv4
    event_type: "session_start",
    event_time: new Date(eventTimeMs).toISOString(),
    session_id: sessionId,
    client_seq: clientSeq,
    device: "web",
    app_version: impressionConfig.appVersion,
  };

  const parsed = sessionStartEventSchema.safeParse(event);
  if (!parsed.success) {
    captureException(parsed.error);
    return;
  }
  enqueue(parsed.data);
};

/** Build (validate) a `session_end` without enqueueing it — callers decide how it ships. */
const buildSessionEnd = (
  sessionId: string,
  clientSeq: number,
  eventTimeMs: number,
  appVersion: string,
): SessionEndEvent | null => {
  const event: SessionEndEvent = {
    event_id: sessionEndEventId(sessionId), // DETERMINISTIC — collapses repeated ends
    event_type: "session_end",
    event_time: new Date(eventTimeMs).toISOString(),
    session_id: sessionId,
    client_seq: clientSeq,
    device: "web",
    app_version: appVersion,
  };

  const parsed = sessionEndEventSchema.safeParse(event);
  if (!parsed.success) {
    captureException(parsed.error);
    return null;
  }
  return parsed.data;
};

const emitSessionEnd = (
  sessionId: string,
  clientSeq: number,
  eventTimeMs: number,
  appVersion: string,
): SessionEndEvent | null => {
  const event = buildSessionEnd(sessionId, clientSeq, eventTimeMs, appVersion);
  if (!event) return null;
  enqueue(event);
  return event;
};

// ── Session lifecycle ──

/** Mint a fresh session and emit its `session_start` (client_seq 0, the session's first event). */
const mintSession = (now: number): SessionState => {
  const id = uuidv4();
  emitSessionStart(id, 0, now);
  const state: SessionState = { id, lastActivityAt: now, seq: 1, seen: [] };
  writeSession(state);
  registerSession(id, now, state.seq);
  return state;
};

/**
 * End an idle-expired session at its last recorded activity, then drop it from the cross-tab
 * registry. The deterministic id keeps this safe against a leave-time or deferred end of the same
 * session.
 */
const endIdleSession = (session: SessionState): void => {
  emitSessionEnd(
    session.id,
    session.seq,
    session.lastActivityAt,
    impressionConfig.appVersion,
  );
  unregisterSession(session.id);
};

/**
 * Return the live session, minting a new one (with a `session_start`) when absent, or rotating —
 * end the stale one at its last activity, start a new one — after the idle boundary. Resuming
 * within the idle gap returns the same session and emits nothing (AC1).
 */
const ensureSession = (now: number): SessionState => {
  const current = readSession();
  if (!current) return mintSession(now);
  if (now - current.lastActivityAt > impressionConfig.sessionIdleMs) {
    endIdleSession(current);
    return mintSession(now);
  }
  return current;
};

/**
 * Allocate session_id + client_seq + first_impression for one impression, atomically. Rotates the
 * session on the idle boundary (emitting the old end + new start) before allocating, so the seq /
 * seen belong to the right session. first_impression is true the first time a statusId is seen
 * this session, false on a re-view.
 */
const stampEvent = (
  statusId: string,
): { sessionId: string; clientSeq: number; firstImpression: boolean } => {
  const now = Date.now();
  const session = ensureSession(now);

  const firstImpression = !session.seen.includes(statusId);
  const clientSeq = session.seq;

  writeSession({
    id: session.id,
    lastActivityAt: now,
    seq: clientSeq + 1,
    seen: firstImpression
      ? [...session.seen, statusId].slice(-impressionConfig.seenStatusCap)
      : session.seen,
  });
  registerSession(session.id, now, clientSeq + 1);

  return { sessionId: session.id, clientSeq, firstImpression };
};

/**
 * Allocate session_id + client_seq for one engagement event. Shares the same per-session counter
 * as impressions (rotating the session on the idle boundary, same as stampEvent) but does not
 * touch `seen` — first_impression bookkeeping is impression-only and doesn't apply here.
 */
const stampEnvelope = (): { sessionId: string; clientSeq: number } => {
  const now = Date.now();
  const session = ensureSession(now);
  const clientSeq = session.seq;

  writeSession({ ...session, lastActivityAt: now, seq: clientSeq + 1 });
  registerSession(session.id, now, clientSeq + 1);

  return { sessionId: session.id, clientSeq };
};

// ── Leave + deferred recovery ──

/**
 * Emit this tab's `session_end` at the leave moment (tab hide / route away / unload — sessions
 * AC-W1). The event is enqueued (persisted) for the normal / next-load flush, and returned so the
 * caller can also fire the one keepalive attempt that survives the page going away. The session is
 * NOT torn down — a return within the idle gap continues it and a later leave re-emits the same-id
 * end, so the server keeps the latest. Last-activity is frozen at the leave moment so a deferred
 * sweep of this same session reproduces a byte-identical end.
 */
const endSessionOnLeave = (now: number): SessionEndEvent | null => {
  const session = readSession();
  if (!session) return null;

  const event = emitSessionEnd(
    session.id,
    session.seq,
    now,
    impressionConfig.appVersion,
  );
  if (!event) return null;

  const seq = session.seq + 1; // consume the seq so a post-resume impression can't reuse it
  writeSession({ ...session, seq, lastActivityAt: now });
  registerSession(session.id, now, seq);
  return event;
};

/**
 * On load, mint deferred `session_end`s for sessions whose tab went away without emitting one
 * (close / force-kill / crash) — any registry entry idle past the boundary that isn't this tab's
 * own live session (sessions AC-W2). Concurrent sweeps from multiple tabs re-emit the same
 * deterministic id and collapse server-side, so best-effort registry writes are safe.
 */
const sweepAbandonedSessions = (now: number): void => {
  const registry = readRegistry();
  const currentId = readSession()?.id;
  let changed = false;

  for (const [id, record] of Object.entries(registry)) {
    if (id === currentId) continue; // our own session — ensureSession handles it
    if (now - record.lastActivityAt <= impressionConfig.sessionIdleMs) continue; // still live
    emitSessionEnd(id, record.seq, record.lastActivityAt, record.appVersion);
    delete registry[id];
    changed = true;
  }

  if (changed) writeRegistry(registry);
};

/**
 * Startup: recover abandoned sessions, then resume this tab's session (no new start within the
 * idle gap — AC1) or mint one. The page load itself counts as activity, so a concurrent sweep from
 * another tab won't treat this freshly-resumed session as abandoned.
 */
const initSession = (): void => {
  if (typeof window === "undefined") return;
  const now = Date.now();
  sweepAbandonedSessions(now);
  const session = ensureSession(now);
  writeSession({ ...session, lastActivityAt: now });
  registerSession(session.id, now, session.seq);
};

export {
  stampEvent,
  stampEnvelope,
  initSession,
  endSessionOnLeave,
  sessionEndEventId,
  SESSION_END_NAMESPACE,
};
